/**
 * Web Push subscription helpers.
 *
 * iOS 16.4+ requires the app to be launched from the Home Screen
 * (display-mode: standalone) before push permission can be requested.
 */
import api from "./api";

const b64ToUint8 = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const s = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(s);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const pushSupport = () => {
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
  const iosReady = !isIOS || isStandalone;
  return { supported, isIOS, isStandalone, iosReady };
};

export const currentSubscription = async () => {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
};

export const subscribePush = async () => {
  const { supported, iosReady } = pushSupport();
  if (!supported) throw new Error("This browser doesn't support web push.");
  if (!iosReady) throw new Error("On iOS, tap Share → \u201CAdd to Home Screen\u201D, then open Boardroom from your home screen to enable notifications.");

  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission denied.");

  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/vapid-public-key");
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: b64ToUint8(data.key),
  });
  await api.post("/push/subscribe", { subscription: sub.toJSON(), ua: navigator.userAgent });
  return sub;
};

export const unsubscribePush = async () => {
  const sub = await currentSubscription();
  if (sub) {
    try {
      await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
    } catch (err) {
      // Server may already have pruned this endpoint — ignore.
      console.debug("push unsubscribe (server) failed", err);
    }
    await sub.unsubscribe();
  } else {
    await api.post("/push/unsubscribe", {});
  }
};

export const sendPushTest = async () => {
  const { data } = await api.post("/push/test");
  return data;
};
